import type { CustomerSpdPresence, CustomerSpdValues } from '../../../types/customerSpd.types';

/** The SPD fields, in render order. `driversLicenseState` is a plain code (not sensitive). */
export const SPD_FIELDS = [
    'socialSecurityNumber',
    'dateOfBirth',
    'driversLicenseNumber',
    'driversLicenseState',
    'driversLicenseExpirationDate'
] as const;

/** Text mask shown for SSN and DL number while masked. */
export const SPD_TEXT_MASK = '*********';

/** Interstate input masking pattern for SSN (###-##-####). */
export const SSN_PATTERN = {
    guide: true,
    mask: [/./, /./, /./, '-', /./, /./, '-', /./, /./, /./, /./],
    pipe: undefined,
    unmask: ['-']
};

/**
 * Derive presence flags (which fields have a value) from a set of SPD values.
 * `driversLicenseState` carries its value through (it is not sensitive); the rest become booleans.
 * Used by the reducer to commit presence on save success.
 */
export const toPresenceFlags = (values: CustomerSpdValues): CustomerSpdPresence => ({
    socialSecurityNumber: Boolean(values.socialSecurityNumber),
    dateOfBirth: Boolean(values.dateOfBirth),
    driversLicenseNumber: Boolean(values.driversLicenseNumber),
    driversLicenseState: values.driversLicenseState ?? null,
    driversLicenseExpirationDate: Boolean(values.driversLicenseExpirationDate),
    bankAccountNumber: Boolean(values.bankAccountNumber)
});

/** All-false presence (state code null). Used when the backend reports no SPD (404 / ok:false). */
export const emptyPresence = (): CustomerSpdPresence => toPresenceFlags({});

const isBlank = (value: unknown): boolean => value == null || value === '';

const MERGEABLE_FIELDS = [...SPD_FIELDS, 'bankAccountNumber'] as const;
type MergeableField = (typeof MERGEABLE_FIELDS)[number];

export const mergeNonEmptyFields = (fetched: CustomerSpdValues, edits: CustomerSpdValues = {}): CustomerSpdValues => {
    const result: CustomerSpdValues = { ...edits };
    for (const field of MERGEABLE_FIELDS) {
        if (result[field] == null && !isBlank(fetched[field])) {
            (result as Record<MergeableField, string | null | undefined>)[field] = fetched[field];
        }
    }
    return result;
};

/**
 * Returns true when any SPD field in `edited` differs from the corresponding field in `fetched`.
 * Used to determine whether the save payload should include an SPD entry for this participant.
 */
export const hasSpdChanged = (edited: CustomerSpdValues, fetched: CustomerSpdValues | null): boolean =>
    SPD_FIELDS.some((field) => edited[field] != null && edited[field] !== fetched?.[field]);
